import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getRephrasedText } from 'services/sysaiService';
import { PENDO_TRACK_EVENTS } from 'constants/index';
import usePendoTrackEvents from 'common/utils/hooks/usePendoTrackEvents';
import { countWords, formatLineBreaks } from './utils';
import {
  CONTAINER_PADDING,
  FROALA_SELECTION_END_MARKER,
  FROALA_SELECTION_START_MARKER,
  MINIMUM_WORDS_FOR_REPHRASE,
  TOOLBAR_HEIGHT,
} from './constants';

export const useRephraseToolbar = ({ fieldName, editorRef, editorContainerRef, updateCharCount, isTemplatePage, srId }) => {
  const [canRevert, setCanRevert] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const lastClickedActionRef = useRef();
  const prevHtmlRef = useRef('');
  const editor = editorRef.current;
  const toolbarRef = useRef();
  const abortControllerRef = useRef(new AbortController());
  const { isLoading, mutate } = useMutation(getRephrasedText);
  const pendoTrackEvents = usePendoTrackEvents();
  const handleSelectionChange = useCallback(() => {
      const { selection } = editor;

      if (selection?.inEditor() && countWords(selection.text()) >= MINIMUM_WORDS_FOR_REPHRASE) {
        const editorRect = editorContainerRef.current.getBoundingClientRect();
        const selectionRect = editor.position.getBoundingRect();
        const rectTopDiff = Math.max(selectionRect.top - editorRect.top, CONTAINER_PADDING) - TOOLBAR_HEIGHT;
        //   const rectLeftDiff = selectionRect.left - editorRect.left;
        /* It's possible to use just the selection bounding rect but then you'd have to use fixed positioning which breaks when someone
      decidce to wrap the component in some container with the transform css rule. In order to avoid that I've used absolute positioning
      this way the element is positioned relative to the immediate container(.richtext-wrap in this case) which is why I need the container
      bounding rect in here.*/
        setShowToolbar(true);

        setToolbarPosition({
          top: rectTopDiff,
          // left: rectLeftDiff,
        });
    } else {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
      setCanRevert(false);
      setShowToolbar(false);
    }
  }, [editor, editorContainerRef, setToolbarPosition]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  const saveOriginalHtmlWithSelection = () => {
    if (!editor) return;

    const { selection: selectionAPI, markers } = editor;
    // Place markers to allow restoring selection after replacing the text
    const selectedRange = selectionAPI.ranges(0);
    markers.place(selectedRange, true, 0);
    markers.place(selectedRange, false, 0);
    prevHtmlRef.current = editor.html.get(true);
  };

  const handleClick = async (rephraseType) => {
    lastClickedActionRef.current = rephraseType;

    if (!editor) return;

    pendoTrackEvents(PENDO_TRACK_EVENTS.REPHRASE, { srField: fieldName, rephraseOption: rephraseType });

    const selectionAPI = editor.selection;
    const selectedText = selectionAPI.text();

    if (!canRevert) {
      saveOriginalHtmlWithSelection();
    }

    const { signal } = abortControllerRef.current;

    const rephraseRequest = { type: rephraseType, text: selectedText };

    if (!isTemplatePage) {
      // Include srId only if rephrase came from queue/ticket page
      rephraseRequest.srId = srId;
    }

    mutate(
      { rephraseRequest, signal },
      {
        onSuccess: (data) => {
          const { text: rephrasedText } = data;

          editor.commands.clearFormatting();
          // Insert the result surrounded with selection markers to select the text after inserting it,
          // this is the cleanest solution to do so without looking for the DOM node
          // in which the selection needs to start and calculating the correct offset
          editor.html.insert(
            `${FROALA_SELECTION_START_MARKER}${formatLineBreaks(rephrasedText)}${FROALA_SELECTION_END_MARKER}`,
          );

          updateCharCount();

          selectionAPI.restore();

          setCanRevert(true);
        },
      },
    );
  };

  const revert = () => {
    if (!editor) return;

    if (canRevert) {
      setCanRevert(false);
      editor.html.set(prevHtmlRef.current);
      updateCharCount();
      editor.selection.restore();
    }
  };

  const preventTextSelectionResetWhenButtonsDisabled = (e) => e.preventDefault();

  return {
    toolbarProps: {
      toolbarRef,
      position: toolbarPosition,
      lastClickedAction: lastClickedActionRef.current,
      isLoading,
      canRevert,
      handleClick,
      revert,
      preventTextSelectionResetWhenButtonsDisabled,
    },
    showToolbar,
  };
};
